import DocumentIconSvg from 'virtual:icons/material-symbols/description?raw';
import GlobeIconSvg from 'virtual:icons/material-symbols/language?raw';

/**
 * Regular expression to match citations in format: (CID:42, "quote") or (CID:42)
 * Supports:
 * - Case insensitive CID (cid, CID, Cid, etc.)
 * - Both parentheses () and square brackets []
 * - Multiple quote styles: "text", „text", 'text', 'text', «text», "text"
 * - Multiple CIDs in one bracket set (separated by comma or semicolon)
 * - Handles U+201C (") left double quote used as closing in some documents
 * - Quotes can contain parentheses, apostrophes, and other characters
 * Captures: CID number and optional quote (in one of three capture groups depending on quote style)
 *
 * IMPORTANT: Uses separate patterns for double vs single quotes to handle apostrophes correctly:
 * - Double quotes ("...") can contain apostrophes like "doesn't"
 * - Straight single quotes ('...') exclude the straight apostrophe character
 * - Fancy single quotes ('...') use greedy matching since apostrophe and closing quote are same character (')
 */
export const citationRx =
	/(?:[\(\[]|[,;]\s*)(?:cid):(\d+)(?:\s*[,;](?=\s*["\u201C\u201E'\u2018\u00AB])\s*(?:["\u201C\u201E]([^"\u201C\u201D]*)["\u201C\u201D]|'([^']*)'|[\u2018]([^\u2018)\]]*)[\u2019]|\u00AB([^\u00BB]*)\u00BB))?[\)\]]?/gi;

/**
 * Regular expression to match web citations in format: (WEB:3, "quote") or (WEB:3)
 * Same structure as citationRx but with WEB prefix instead of CID.
 */
export const webCitationRx =
	/(?:[\(\[]|[,;]\s*)(?:web):([a-f0-9]{8})(?:\s*[,;](?=\s*["\u201C\u201E'\u2018\u00AB])\s*(?:["\u201C\u201E]([^"\u201C\u201D]*)["\u201C\u201D]|'([^']*)'|[\u2018]([^\u2018)\]]*)[\u2019]|\u00AB([^\u00BB]*)\u00BB))?[\)\]]?/gi;

export interface ExtractedWebCitation {
	messageId: string;
	startIndex: number;
	length: number;
	webId: string;
	quote?: string;
}

/**
 * Extract web citations from messages with exact position info.
 */
export function extractWebCitationsWithPositions(
	messages: Array<{ id: string; content: string }>
): ExtractedWebCitation[] {
	const citations: ExtractedWebCitation[] = [];

	for (const message of messages) {
		if (!message.content) continue;

		let match;
		webCitationRx.lastIndex = 0;
		while ((match = webCitationRx.exec(message.content)) !== null) {
			const rawQuote = match[2] || match[3] || match[4] || match[5];
			citations.push({
				messageId: message.id,
				startIndex: match.index,
				length: match[0].length,
				webId: match[1],
				quote: rawQuote && stripMarkdownStyling(rawQuote)
			});
		}
	}

	return citations;
}

/**
 * Strip leading and trailing markdown styling characters from a quote.
 * Handles bold, italic, strikethrough, and inline code markers.
 */
function stripMarkdownStyling(quote: string): string {
	// Patterns to strip from both ends (order matters - check longer patterns first)
	const patterns = [
		/^\*\*/,
		/\*\*$/, // bold **
		/^__/,
		/__$/, // bold __
		/^~~/,
		/~~$/, // strikethrough
		/^\*/,
		/\*$/, // italic *
		/^_/,
		/_$/, // italic _
		/^`/,
		/`$/ // inline code
	];

	let result = quote;
	let changed = true;

	// Keep stripping until no more changes (handles nested like ***bold italic***)
	while (changed) {
		changed = false;
		for (const pattern of patterns) {
			const newResult = result.replace(pattern, '');
			if (newResult !== result) {
				result = newResult;
				changed = true;
			}
		}
	}

	return result.trim();
}

/**
 * Extracted citation with exact position info for precise replacement
 */
export interface ExtractedCitation {
	messageId: string;
	startIndex: number;
	length: number;
	cid: number;
	quote?: string;
}

/**
 * Extract citations from messages with exact position info.
 * Returns citations with messageId, startIndex, and length for precise replacement.
 */
export function extractCitationsWithPositions(
	messages: Array<{ id: string; content: string }>
): ExtractedCitation[] {
	const citations: ExtractedCitation[] = [];

	for (const message of messages) {
		if (!message.content) continue;

		let match;
		citationRx.lastIndex = 0;
		while ((match = citationRx.exec(message.content)) !== null) {
			const rawQuote = match[2] || match[3] || match[4] || match[5];
			citations.push({
				messageId: message.id,
				startIndex: match.index,
				length: match[0].length,
				cid: parseInt(match[1], 10),
				quote: rawQuote && stripMarkdownStyling(rawQuote)
			});
		}
	}

	return citations;
}

/**
 * Inline quote with all info needed for both display (highlighting) and replacement (badges)
 */
export interface InlineQuote {
	// Display info
	citation_index: number;
	citation_color: string;
	sub_index: string;
	quotedText?: string;
	chunkText?: string; // full chunk text for highlighting in source panel

	// Replacement info (from ExtractedCitation)
	messageId: string;
	startIndex: number;
	length: number;
	cid: number | string; // number for document sources, webId string for web sources

	citationType: 'document' | 'web';
}

/**
 * Normalize Unicode characters to their canonical ASCII equivalents for matching
 * This helps match quotes when LLMs use different Unicode variants than the source
 * @param text - The text to normalize
 * @returns Normalized text with canonical character forms
 */
function normalizeUnicodeForMatching(text: string): string {
	return (
		text
			// Normalize all hyphen/dash variants to regular hyphen-minus (U+002D)
			.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, '-')
			// Normalize all space variants to regular space (U+0020)
			.replace(/[\u00A0\u202F\u2000-\u200B]/g, ' ')
			// Normalize quote marks to ASCII equivalents
			.replace(/[\u201C\u201D\u201E\u201F]/g, '"') // All double quotes to "
			.replace(/[\u2018\u2019\u201A\u201B]/g, "'") // All single quotes to '
			// Normalize other common variants
			.replace(/[\u00AB\u00BB]/g, '"')
	); // Guillemets to "
}

/**
 * Escape special regex characters in a string and replace whitespace with \s+ pattern
 * Useful for creating flexible regex patterns for text matching
 * @param text - The text to escape for use in a regex pattern
 * @returns Escaped pattern with flexible whitespace matching
 */
function escapeRegexPattern(text: string): string {
	return text
		.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape special regex chars
		.replace(/\s+/g, '\\s+'); // Replace whitespace with \s+ pattern
}

/**
 * Find quote in text with whitespace-insensitive matching
 * Returns the actual text from the source if found, or multiple ranges if ellipsis is present
 */
function findQuoteInText(
	quote: string,
	text: string
):
	| { index: number; actualText: string }
	| { ranges: Array<{ start: number; end: number }> }
	| null {
	// Check if quote contains ellipsis
	const hasEllipsis = /\.{3}|…/.test(quote);

	if (hasEllipsis) {
		// Split by ellipsis (... or unicode ellipsis character)
		const parts = quote
			.split(/\.{3}|…/)
			.map((p) => p.trim())
			.filter((p) => p.length > 0);

		if (parts.length === 0) return null;

		// Find each part sequentially in the text
		const ranges: Array<{ start: number; end: number }> = [];
		let searchStartIndex = 0;

		for (const part of parts) {
			const remainingText = text.substring(searchStartIndex);
			const partMatch = findSingleQuotePart(part, remainingText);

			if (partMatch) {
				const absoluteStart = searchStartIndex + partMatch.index;
				const absoluteEnd = absoluteStart + partMatch.actualText.length;
				ranges.push({ start: absoluteStart, end: absoluteEnd });
				// Next search starts after this match
				searchStartIndex = absoluteEnd;
			} else {
				// Part not found - if we found at least one part, return what we have
				if (ranges.length > 0) {
					return { ranges };
				}
				// No parts found at all
				return null;
			}
		}

		// All parts found
		return { ranges };
	}

	// No ellipsis - use simple matching
	return findSingleQuotePart(quote, text);
}

/**
 * Find a single quote part (without ellipsis) in text
 */
function findSingleQuotePart(
	quote: string,
	text: string
): { index: number; actualText: string } | null {
	// Strategy 1: Exact match
	const exactIndex = text.indexOf(quote);
	if (exactIndex !== -1) {
		return { index: exactIndex, actualText: quote };
	}

	// Strategy 2: Case-insensitive match
	const lowerText = text.toLowerCase();
	const lowerQuote = quote.toLowerCase();
	const caseInsensitiveIndex = lowerText.indexOf(lowerQuote);
	if (caseInsensitiveIndex !== -1) {
		const actualText = text.substring(caseInsensitiveIndex, caseInsensitiveIndex + quote.length);
		return { index: caseInsensitiveIndex, actualText };
	}

	// Strategy 3: Whitespace-insensitive regex match
	// Create a regex pattern where each space in the quote matches any whitespace
	const escapedQuote = escapeRegexPattern(quote);

	const regex = new RegExp(escapedQuote, 'i'); // Case-insensitive
	const match = text.match(regex);

	if (match && match.index !== undefined) {
		return {
			index: match.index,
			actualText: match[0] // The actual matched text from the source
		};
	}

	// Strategy 4: Unicode-normalized matching
	// Handles cases where LLM uses U+2011 (non-breaking hyphen) but source has U+002D (regular hyphen)
	// or other visually similar but technically different Unicode characters
	const normalizedQuote = normalizeUnicodeForMatching(quote.toLowerCase());
	const normalizedText = normalizeUnicodeForMatching(text.toLowerCase());

	const normalizedIndex = normalizedText.indexOf(normalizedQuote);
	if (normalizedIndex !== -1) {
		// Since normalizeUnicodeForMatching only does 1:1 character replacements (no insertions/deletions),
		// the character positions remain the same. We can directly use the index from normalized text.
		const actualText = text.substring(normalizedIndex, normalizedIndex + quote.length);
		return { index: normalizedIndex, actualText };
	}

	return null;
}

/**
 * Expand a text range to encompass the full sentence
 * Finds sentence boundaries around the given range
 * Stops at markdown syntax elements to avoid breaking formatting
 */
function expandToFullSentence(
	text: string,
	startIndex: number,
	endIndex: number
): { start: number; end: number } {
	// Sentence boundary characters:
	// - Standard: . ! ? followed by whitespace
	// - Colon/semicolon: : ; followed by whitespace (common in legal/parliamentary text)
	// - Em-dash: — (U+2014) followed by whitespace
	// - Newlines
	const sentenceEnders = /[.!?:;—]\s|[\n\r]/;

	// Markdown reserved symbols that should stop expansion
	// Matches: list items (-, *, +), headers (#), blockquotes (>), code blocks (```), horizontal rules (---, ***)
	const markdownBoundaries = /^[-*+#>][\s]|^```|^---+|^\*\*\*+/m;

	// Find sentence start by looking backwards for sentence ender or start of text
	let sentenceStart = startIndex;
	for (let i = startIndex - 1; i >= 0; i--) {
		const twoChars = text.substring(i, i + 2);
		if (sentenceEnders.test(twoChars)) {
			// Check what kind of sentence ender we matched
			const char = text[i];
			if (char === '\n' || char === '\r') {
				// Newline is 1 character, start after it
				sentenceStart = i + 1;
			} else {
				// Sentence ender + space is 2 characters, start after both
				sentenceStart = i + 2;
			}
			break;
		}

		// Check if we hit a markdown boundary
		// Look at the start of the current line
		const lineStart = text.lastIndexOf('\n', i) + 1;
		const lineContent = text.substring(lineStart, i + 1).trim();
		if (markdownBoundaries.test(lineContent)) {
			sentenceStart = i + 1; // Stop before the markdown symbol
			break;
		}

		if (i === 0) {
			sentenceStart = 0; // Reached start of text
		}
	}

	// Find sentence end by looking forwards for sentence ender or end of text
	let sentenceEnd = endIndex;
	for (let i = endIndex; i < text.length - 1; i++) {
		const twoChars = text.substring(i, i + 2);
		if (sentenceEnders.test(twoChars)) {
			sentenceEnd = i + 1; // Include the sentence ender
			break;
		}
	}

	// If loop completed without finding a sentence ender, extend to end of text
	if (sentenceEnd === endIndex && endIndex < text.length) {
		sentenceEnd = text.length;
	}

	return { start: sentenceStart, end: sentenceEnd };
}

/**
 * Creates a reference badge span element for highlighting in the document
 */
export function createHighlightRefBadge(quote: InlineQuote, includeRefIndex = false): string {
	const refIndexAttr =
		includeRefIndex && quote.citation_index ? `data-ref-index="${quote.citation_index}" ` : '';
	return `<div ${refIndexAttr}data-ref-id="${quote.cid}" style="background-color: ${quote.citation_color}" class="source-ref"><span>${quote.citation_index}${quote.sub_index}</span></div>`;
}

/**
 * Creates an inline citation badge for chat messages (primary source)
 */
export function createMessageRefBadge(quote: InlineQuote): string {
	return `<div data-ref-index="${quote.citation_index}" data-ref-id="${quote.cid}" style="background-color: ${quote.citation_color}" class="source-ref">${DocumentIconSvg}<span>${quote.citation_index}${quote.sub_index}</span></div>`;
}

/**
 * Creates an inline citation badge for chat messages (web source)
 */
export function createMessageWebSourceRefBadge(quote: InlineQuote): string {
	return `<div data-web-source-id="${quote.cid}" style="background-color: ${quote.citation_color}" class="web-source-ref">${GlobeIconSvg}<span>${quote.citation_index}${quote.sub_index}</span></div>`;
}

/**
 * Creates a highlighted text span element
 */
export function createHighlightedText(text: string, color: string): string {
	return text
		.split(' |')
		.map(
			(part) => `<span class="highlight" style="background-color: ${color}">${part.trim()}</span>`
		)
		.join(' |');
}

/**
 * Transform source document text to highlight cited passages
 * Uses quote matching to find and highlight the relevant text
 */
export function transformSourceText(text: string, quotes: InlineQuote[]): string {
	const transformed = quotes.reduce((currentText, quote) => {
		const refBadge = createHighlightRefBadge(quote);

		// Strategy 1: If we have a quoted text, try to find and highlight the full sentence containing it
		if (quote.quotedText) {
			const match = findQuoteInText(quote.quotedText, currentText);
			if (match) {
				// Handle ellipsis quotes (multiple ranges)
				if ('ranges' in match) {
					// Quote contains ellipsis - expand each range to its sentence
					const expandedRanges = match.ranges.map((range) =>
						expandToFullSentence(currentText, range.start, range.end)
					);

					// Find the overall start and end (from first sentence to last sentence)
					const overallStart = Math.min(...expandedRanges.map((r) => r.start));
					const overallEnd = Math.max(...expandedRanges.map((r) => r.end));

					const before = currentText.substring(0, overallStart);
					const highlightedText = currentText.substring(overallStart, overallEnd);
					const after = currentText.substring(overallEnd);

					return (
						before + refBadge + createHighlightedText(highlightedText, quote.citation_color) + after
					);
				} else {
					// Simple quote without ellipsis
					const quoteStart = match.index;
					const quoteEnd = match.index + match.actualText.length;
					const { start: sentenceStart, end: sentenceEnd } = expandToFullSentence(
						currentText,
						quoteStart,
						quoteEnd
					);

					const before = currentText.substring(0, sentenceStart);
					const sentence = currentText.substring(sentenceStart, sentenceEnd);
					const after = currentText.substring(sentenceEnd);

					return before + refBadge + createHighlightedText(sentence, quote.citation_color) + after;
				}
			}
		}

		// Strategy 2: Try to find the full chunk text
		if (quote.chunkText && currentText.includes(quote.chunkText)) {
			return currentText.replace(quote.chunkText, (substr: string) =>
				substr
					.split('\n')
					.map((p: string) => refBadge + createHighlightedText(p, quote.citation_color))
					.join('\n\n')
			);
		}

		// Strategy 3: Try first N characters of chunk text
		const PREVIEW_LENGTH = 150;
		if (quote.chunkText) {
			const preview = quote.chunkText.substring(0, PREVIEW_LENGTH).trim();
			if (currentText.includes(preview)) {
				return currentText.replace(
					preview,
					(matched: string) => refBadge + createHighlightedText(matched, quote.citation_color)
				);
			}
		}

		// Strategy 4: No match found - just add reference badge at start
		return refBadge + currentText;
	}, text);

	// Replace single newlines with double newlines for markdown (but preserve existing multiple newlines)
	// Only matches newlines that are neither preceded nor followed by another newline
	return transformed.replace(/(?<!\n)\n(?!\n)/g, '\n\n');
}

/**
 * Transform a chat message by replacing citation markers with inline source badges.
 * Processes citations in reverse order to preserve string indices during replacement.
 * Skips overlapping citations to avoid broken HTML.
 */
export function transformMessage(
	allInlineQuotes: InlineQuote[],
	message: string,
	messageId: string
): string {
	const messageQuotes = allInlineQuotes
		.filter((q) => q.messageId === messageId)
		.sort((a, b) => b.startIndex - a.startIndex);

	let result = message;
	let safeIndex = message.length;

	for (const q of messageQuotes) {
		const quoteEnd = q.startIndex + q.length;
		if (quoteEnd > safeIndex) continue;

		const badge = q.citationType === 'web'
			? createMessageWebSourceRefBadge(q)
			: createMessageRefBadge(q);
		result = result.slice(0, q.startIndex) + badge + result.slice(quoteEnd);
		safeIndex = q.startIndex;
	}

	return result;
}
